import { zodResolver } from '@hookform/resolvers/zod';
import {
  Action,
  Alert,
  Card,
  ColorInput,
  Page,
  SimpleSelect,
  SwitchInput,
  TextAreaInput,
  TextInput,
  UrlInput,
  FloatingSave,
} from 'corso-admin-ui';
import { DbJsonSchema } from 'corso-types';
import { FormEventHandler } from 'react';
import { Controller, useForm } from 'react-hook-form';
import ClipboardButton from '~/components/ClipboardButton';
import SettingsAnchorCard from '~/features/settings/components/SettingsAnchorCard';
import { useConfigSettings } from '~/hooks/useConfigSettings';
import useIsCorsoAdmin from '~/hooks/useIsCorsoAdmin';
import {
  useThemeSettings,
  useThemeSettingsUpdate,
} from '~/hooks/useThemeSettings';
import { themeSettingsFormSchema } from '~/types';

const formId = 'theme-settings';

export default function ThemeSettingsPage() {
  const { data } = useThemeSettings();
  const { mutateAsync: saveChanges } = useThemeSettingsUpdate();

  const isCorsoAdmin = useIsCorsoAdmin();

  const { data: isClaimLineageEnabled } = useConfigSettings(
    ({ variantExchanges }) => variantExchanges.isClaimLineageEnabled,
  );

  const warrantyProgramNameOptions = Object.values(
    DbJsonSchema.StoreUiConfig.WarrantyProgramName,
  ).map((name) => ({
    value: name,
    label: name,
  }));

  const {
    register,
    formState: { errors, isDirty, isSubmitting },
    reset,
    control,
    handleSubmit,
  } = useForm({
    resolver: zodResolver(themeSettingsFormSchema),
    // TODO revisit to revise to default values and improve color input
    // * intentionally not using `defaultValues`, since the color inputs must be controlled to stay in sync
    values: {
      merchantPrimaryColor: data?.merchantPrimaryColor ?? '#000000',
      merchantBackgroundColor: data?.merchantBackgroundColor ?? '#ffffff',
      merchantBackgroundUrl: data?.merchantBackgroundUrl ?? '',
      merchantLogoUrl: data?.merchantLogoUrl ?? '',
      merchantFaviconUrl: data?.merchantFaviconUrl ?? '',
      footerText: data?.footerText ?? '',
      orderLookupDetailText: data?.orderLookupDetailText ?? '',
      orderLookupGiftReturnDetailText:
        data?.orderLookupGiftReturnDetailText ?? '',
      merchantPageTitle: data?.merchantPageTitle ?? '',
      orderNumberDescriptionText: data?.orderNumberDescriptionText ?? '',
      noReturnRequiredItemsDetailText:
        data?.noReturnRequiredItemsDetailText ?? '',
      warrantyItemsDetailText: data?.warrantyItemsDetailText ?? '',
      offerGiftCardDetailText: data?.offerGiftCardDetailText ?? '',
      offerRefundDetailText: data?.offerRefundDetailText ?? '',
      noReturnRequiredItemsHeadline: data?.noReturnRequiredItemsHeadline ?? '',
      hideLabelDownloadWhenQrCodeIsPresent:
        data?.hideLabelDownloadWhenQrCodeIsPresent ?? false,
      shouldPrioritizeCredit: data?.shouldPrioritizeCredit ?? false,
      creditHeadlineText: data?.creditHeadlineText ?? '',
      creditDetailText: data?.creditDetailText ?? '',
      isReturnPhoneRequired: data?.isReturnPhoneRequired ?? false,
      isWarrantyPhoneRequired: data?.isWarrantyPhoneRequired ?? false,
      warrantyProgramName: data?.warrantyProgramName ?? 'Warranty',
      orderHubStartRequestTitleText: data?.orderHubStartRequestTitleText ?? '',
      orderHubStartRequestDetailText:
        data?.orderHubStartRequestDetailText ?? '',
      orderLookupRegistrationText: data?.orderLookupRegistrationText ?? '',
      emailMarketingConsentText: data?.emailMarketingConsentText ?? '',
      emailMarketingConsentDescriptionText:
        data?.emailMarketingConsentDescriptionText ?? '',
      emailMarketingConsentPolicyUrlTextTitle:
        data?.emailMarketingConsentPolicyUrlTextTitle ?? '',
      identifyProductUrl: data?.identifyProductUrl ?? '',
      identifyProductUrlButtonText: data?.identifyProductUrlButtonText ?? '',
      identifyProductDetailText: data?.identifyProductDetailText ?? '',
      identifyProductFormPlaceholder:
        data?.identifyProductFormPlaceholder ?? '',
      registrationSubmissionText: data?.registrationSubmissionText ?? '',
      originatingOrderCreditTitleText:
        data?.originatingOrderCreditTitleText ?? '',
      originatingOrderCreditDetailText:
        data?.originatingOrderCreditDetailText ?? '',
      orderEditDescriptionText: data?.orderEditDescriptionText ?? '',
      orderEditExpiredDescriptionText:
        data?.orderEditExpiredDescriptionText ?? '',
      customCss: data?.customCss ?? '',
    },
  });

  const embeddedUiSnippet = data?.customerAppUrl
    ? `<script type="module" src="https://cdn.corso.com/js/corso-embedded-ui.js?appUrl=${encodeURIComponent(data.customerAppUrl)}"></script>`
    : null;

  const submitHandler: FormEventHandler = (event) => {
    handleSubmit(
      (updates) => saveChanges(updates),
      (formErrors) => {
        console.error('Theme Settings Form Submission Errors:', formErrors);
      },
    )(event).catch(console.error);
  };

  return (
    <Page title="Theme">
      <form
        id={formId}
        className="flex flex-col gap-4"
        onSubmit={submitHandler}
        onReset={(e) => {
          e.preventDefault();
          reset();
        }}
      >
        <FloatingSave
          isDirty={isDirty}
          isSubmitting={isSubmitting}
          form={formId}
        />
        <SettingsAnchorCard id="customer-portal-url">
          <Card.Heading>Customer Portal URL</Card.Heading>
          <UrlInput
            id="customer-portal"
            label="Customer Portal URL"
            labelVisuallyHidden
            details="Use this link to direct users to the customer portal. Reach out to us if you would like to modify this URL."
            // maybe someday allow editing the slug in the url
            value={data?.customerAppUrl ?? ''}
            required
            readOnly
            disabled
            addon={{
              insideEnd: (
                <>
                  {/* // ? adjust to be styled as a button group  */}
                  <ClipboardButton
                    accessibilityLabel="Copy Customer Portal URL"
                    variant="ghost"
                    onClick={() => data?.customerAppUrl ?? ''}
                  />
                  <Action
                    variant="ghost"
                    accessibilityLabel="Visit"
                    icon="externalLink"
                    to={data?.customerAppUrl ?? ''}
                    target="_blank"
                  />
                </>
              ),
            }}
          />

          {embeddedUiSnippet && (
            <>
              <Card.Heading>Customer Portal Embedded UI</Card.Heading>
              <p className="text-sm">
                Most Shopify stores can embed the portal by adding the embedded
                UI block in the theme editor. For headless stores, use the
                script below to embed the portal. Learn more in our{' '}
                <Action
                  to="https://help.corso.com/customer-portal/embed-your-portal-headless"
                  target="_blank"
                  variant="link"
                  rel="noreferrer"
                >
                  help guide
                </Action>
                .
              </p>
              <TextInput
                id="headless-embed-script"
                label="Headless Embed Script"
                required
                readOnly
                disabled
                value={embeddedUiSnippet}
                addon={{
                  insideEnd: (
                    <ClipboardButton
                      accessibilityLabel="Copy Embed Script"
                      variant="ghost"
                      onClick={() => embeddedUiSnippet}
                    />
                  ),
                }}
              />
            </>
          )}
        </SettingsAnchorCard>
        <SettingsAnchorCard id="branding">
          <Card.Heading as="h2">Branding</Card.Heading>
          {/* the customer portal feels like it should go elsewhere; however, it will remain here until a better place it determined */}

          <Controller
            name="merchantPrimaryColor"
            control={control}
            render={({ field: { onChange, onBlur, ref, disabled, value } }) => (
              // maybe someday this can be revised as an uncontrolled component
              <ColorInput
                id="brand-color"
                label="Brand Color"
                details={
                  <p>
                    This color is used as a background for buttons and other
                    content areas. Choose a dark brand color for best results.{' '}
                    <Action
                      variant="link"
                      to="https://webaim.org/resources/contrastchecker/?fcolor=FFFFFF&bcolor=000000"
                      target="_blank"
                      rel="noreferrer"
                    >
                      Explore dark backgrounds with accessibility in mind.
                    </Action>
                  </p>
                }
                placeholder="#rrggbb"
                required
                // * values will be reflected in the preview once controlled by state
                value={value}
                onChange={onChange}
                onBlur={onBlur}
                error={errors.merchantPrimaryColor?.message}
                disabled={disabled}
                ref={ref}
              />
            )}
          />
          <Controller
            name="merchantBackgroundColor"
            control={control}
            render={({ field: { onChange, onBlur, ref, disabled, value } }) => (
              // maybe someday this can be revised as an uncontrolled component
              <ColorInput
                id="background-color"
                label="Background Color"
                placeholder="#rrggbb"
                required
                // * values will be reflected in the preview once controlled by state
                value={value ?? null}
                onChange={onChange}
                onBlur={onBlur}
                error={errors.merchantBackgroundColor?.message}
                disabled={disabled}
                ref={ref}
              />
            )}
          />
          {/* // TODO re-add image file uploads and revisit component design on mobile for removing file */}
          <UrlInput
            id="brand-background"
            label="Background Image"
            details="If provided, this will override the background color."
            placeholder="https://example.com/image.jpg"
            {...register('merchantBackgroundUrl')}
            error={errors.merchantBackgroundUrl?.message}
          />
          <UrlInput
            id="brand-logo"
            label="Logo"
            details="Required aspect ratio of 2:1. 200px x 100px recommended."
            placeholder="https://example.com/image.jpg"
            required
            {...register('merchantLogoUrl')}
            error={errors.merchantLogoUrl?.message}
          />
          <UrlInput
            id="brand-favicon"
            label="Favicon"
            details="64px x 64px recommended."
            placeholder="https://example.com/image.jpg"
            required
            {...register('merchantFaviconUrl')}
            error={errors.merchantFaviconUrl?.message}
          />
          <TextInput
            // intentionally not required; schema is non-nullish so this may be an empty string
            id="footer-text"
            label="Footer Text"
            details="This will appear in the footer of the customer portal. Often used to display a support email for your company."
            placeholder="support@company.com"
            {...register('footerText')}
            error={errors.footerText?.message}
          />

          {isCorsoAdmin && (
            <>
              <Alert heading="Custom CSS is only visible to Corso admins." />
              <TextAreaInput
                id="custom-css"
                label="Custom CSS"
                details="The styles to apply to the customer portal, if no styles are defined, the default styles will be used."
                {...register('customCss')}
                error={errors.customCss?.message}
              />
            </>
          )}
        </SettingsAnchorCard>
        <SettingsAnchorCard id="order-lookup">
          <Card.Heading as="h2">Order Lookup</Card.Heading>
          <TextInput
            id="page-title"
            label="Page Title"
            details="This will appear in the browser tab of the customer portal. Often used to display the company name or a brief description of the page."
            placeholder="Return, Exchange, and Warranty"
            {...register('merchantPageTitle')}
            error={errors.merchantPageTitle?.message}
          />
          <TextInput
            id="order-lookup-detail-text"
            label="Order Lookup Title"
            details="This will appear on the order lookup page of the customer portal. Often used to display custom instructions, or reminders about the request process."
            placeholder="Refer to our return policy for more information."
            {...register('orderLookupDetailText')}
            error={errors.orderLookupDetailText?.message}
          />
          <TextInput
            id="order-lookup-gift-return-detail-text"
            label="Order Lookup Gift Return Title"
            details="This will appear on the order lookup page of the customer portal when a customer is looking up a gift return."
            placeholder="The original purchaser will not be notified that a return is being made, if you need help locating your order please contact support."
            {...register('orderLookupGiftReturnDetailText')}
            error={errors.orderLookupGiftReturnDetailText?.message}
          />
          <TextInput
            id="order-number-description"
            label="Order Number Description"
            details="This will appear on the order lookup page of the customer portal. Often used to display examples of order numbers."
            placeholder="123456, RS-98765, etc."
            {...register('orderNumberDescriptionText')}
            error={errors.orderNumberDescriptionText?.message}
          />
        </SettingsAnchorCard>

        <SettingsAnchorCard id="order-hub">
          <Card.Heading as="h2">Order Hub</Card.Heading>

          <Controller
            control={control}
            name="isReturnPhoneRequired"
            render={({ field: { onChange, value } }) => (
              <SwitchInput
                id="is-return-phone-required"
                label="Require Phone Number"
                details="When enabled, the phone number will be required to complete a Return."
                checked={value}
                onChange={onChange}
              />
            )}
          />
          <TextInput
            id="order-hub-start-request-title"
            label="Order Hub Title"
            placeholder="Returns & Warranties"
            {...register('orderHubStartRequestTitleText')}
            error={errors.orderHubStartRequestTitleText?.message}
          />
          <TextInput
            id="order-hub-start-request-detail-text"
            label="Order Hub Description"
            details="This will appear on the order hub page of the customer portal. Often used to display custom instructions, or reminders about the request process."
            placeholder="Create a request to return an item or start a warranty claim."
            {...register('orderHubStartRequestDetailText')}
            error={errors.orderHubStartRequestDetailText?.message}
          />
        </SettingsAnchorCard>

        <Card>
          <Card.Heading as="h2">Exchange Product Selection</Card.Heading>
          <Controller
            control={control}
            name="shouldPrioritizeCredit"
            render={({ field: { onChange, value } }) => (
              <SwitchInput
                id="should-prioritize-credit"
                label="Prioritize Gift Card or Refund"
                details="When enabled, the gift card or refund option will be presented before the exchange option."
                checked={value}
                onChange={onChange}
              />
            )}
          />
          <TextInput
            id="credit-headline-text"
            label="Prioritize Gift Card or Refund Headline"
            details="This will appear when a customer is presented the option to proceed with a gift card or refund, in lieu of an exchange."
            placeholder="Not looking to exchange?"
            {...register('creditHeadlineText')}
            error={errors.noReturnRequiredItemsDetailText?.message}
          />

          <TextInput
            id="credit-detail-text"
            label="Prioritize Gift Card or Refund Description"
            details="This will appear when a customer is presented the option to proceed with a gift card or refund, in lieu of an exchange."
            placeholder="Please continue to finish your request."
            {...register('creditDetailText')}
            error={errors.creditDetailText?.message}
          />
        </Card>
        <Card>
          <Card.Heading as="h2">Gift Card Or Refund Selection</Card.Heading>
          <TextAreaInput
            id="offer-gift-card-detail-text"
            label="Gift Card Detail Description"
            details="This will appear on the money back options page when a gift card is offered as one of the options."
            placeholder="Get a gift card for store credit. A redeemable code will be sent via email when your return has been approved."
            {...register('offerGiftCardDetailText')}
            error={errors.offerGiftCardDetailText?.message}
          />
          <TextAreaInput
            id="offer-refund-detail-text"
            label="Refund Detail Description"
            details="This will appear on the money back options page when a refund is offered as one of the options."
            placeholder="Disclaimer: Receive a refund to your original payment method once your return has been approved."
            {...register('offerRefundDetailText')}
            error={errors.offerRefundDetailText?.message}
          />
        </Card>
        {isClaimLineageEnabled && (
          <Card>
            <Card.Heading as="h2">Return Summary</Card.Heading>
            <TextInput
              id="originating-order-credit-title-text"
              label="Unconfirmed Amount Title"
              details="This will appear on the return summary page when the items being returned came from an exchange, so the amount owed is not final."
              placeholder="Refund Amount Pending"
              {...register('originatingOrderCreditTitleText')}
              error={errors.originatingOrderCreditTitleText?.message}
            />
            <TextAreaInput
              id="originating-order-credit-detail-text"
              label="Unconfirmed Amount Description"
              details="This will appear on the return summary page when the items being returned came from an exchange, so the amount owed is not final."
              placeholder="The amount shown is an estimate, and will be confirmed after reviewing your return."
              {...register('originatingOrderCreditDetailText')}
              error={errors.originatingOrderCreditDetailText?.message}
            />
          </Card>
        )}

        <SettingsAnchorCard id="overview">
          <Card.Heading as="h2">Overview</Card.Heading>
          <Controller
            control={control}
            name="hideLabelDownloadWhenQrCodeIsPresent"
            render={({ field: { onChange, value } }) => (
              <SwitchInput
                id="notify-customer"
                label="Hide Label Download"
                details="When enabled, the physical label will be hidden when a QR code is present. This is useful to prevent certain types of return label fraud. "
                checked={value}
                onChange={onChange}
              />
            )}
          />
          <TextInput
            id="no-return-required-submission-headline"
            label="Manual Review Submission Title"
            placeholder="Your request has been submitted for review."
            {...register('noReturnRequiredItemsHeadline')}
            error={errors.noReturnRequiredItemsHeadline?.message}
          />
          <TextInput
            id="no-return-required-text"
            label="Manual Review Submission Description"
            details="This will appear on the overview page after the claim has been submitted on line items that are marked for manual review."
            placeholder="We will be in touch soon regarding your request."
            {...register('noReturnRequiredItemsDetailText')}
            error={errors.noReturnRequiredItemsDetailText?.message}
          />
        </SettingsAnchorCard>

        <SettingsAnchorCard id="warranty-customization">
          <Card.Heading as="h2">Warranty Customization</Card.Heading>
          <Controller
            control={control}
            name="isWarrantyPhoneRequired"
            render={({ field: { onChange, value } }) => (
              <SwitchInput
                id="is-warranty-phone-required"
                label="Require Phone Number"
                details="When enabled, the phone number will be required to complete a Warranty."
                checked={value}
                onChange={onChange}
              />
            )}
          />
          <Controller
            name="warrantyProgramName"
            control={control}
            render={({ field: { onChange, value }, fieldState }) => (
              <SimpleSelect
                label="Program Name"
                details="The customer facing name of the Warranty program."
                options={warrantyProgramNameOptions}
                value={value} // be warned, react-hook-form is lying about the value type, as there's no default value
                onChange={onChange}
                error={fieldState.error?.message}
                required
              />
            )}
          />

          <TextInput
            id="warranty-items-detail-text"
            label="Warranty Submission Text"
            details="This will appear on the overview page after the claim has been submitted on warranty line items."
            placeholder="Thank you for submitting your warranty claim, we will be in touch soon."
            {...register('warrantyItemsDetailText')}
            error={errors.warrantyItemsDetailText?.message}
          />
        </SettingsAnchorCard>
      </form>
    </Page>
  );
}
